import {VerticalType} from 'common/src/modules/clients/clients.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {z} from 'zod';
import {
  AiBrandGuidelinesImageCategory,
  BrandColors,
  BrandGuidelines,
  BrandGuidelinesImageCategory,
  BrandIdentity,
  BrandVoiceGuidelines,
  ImageGuidelines,
} from '../../../brandGuidelines.types';
import {updateLocalBrandGuidelines} from '../../../store/brandGuidelines/brandGuidelines.slice';
import {BrandGuidelinesAgentToolName} from '../brandGuidelinesAgent.enums';

const inputSchema = z.object({
  brandIdentity: z
    .object({
      brandName: z.string().nullable().optional(),
      brandDescription: z.string().nullable().optional(),
      brandPersona: z.string().nullable().optional(),
      brandValues: z.array(z.string()).nullable().optional(),
      targetIndustry: z.nativeEnum(VerticalType).nullable().optional(),
      usageContext: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  brandVoiceGuidelines: z
    .object({
      toneOfVoice: z.array(z.string()).nullable().optional(),
      editorialGuidelines: z.array(z.string()).nullable().optional(),
      editorialRestrictions: z.array(z.string()).nullable().optional(),
      brandPhrases: z.array(z.string()).nullable().optional(),
      grammarAndStyle: z.array(z.string()).nullable().optional(),
    })
    .nullable()
    .optional(),
  imageGuidelines: z
    .array(
      z.object({
        imageCategory: z.nativeEnum(AiBrandGuidelinesImageCategory),
        guidelines: z.array(z.string()),
      }),
    )
    .nullable()
    .nullable()
    .optional(),
  colors: z
    .object({
      colors: z
        .array(
          z.object({
            name: z.string().nullable(),
            color: z.string().nullable(),
            purpose: z.string().nullable(),
          }),
        )
        .nullable()
        .nullable()
        .optional(),
      colorUsageGuidelines: z.array(z.string()).nullable().optional(),
    })
    .nullable()
    .optional(),
});

function updateBrandGuidelinesTool(store: CLStore) {
  async function execute({input}: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const state = store.getState();

      // Get current brand guidelines from local state (editable version)
      const currentBrandGuidelines =
        state.brandGuidelinesPage.brandGuidelines.localData ||
        state.brandGuidelinesPage.brandGuidelines.data;

      if (!currentBrandGuidelines) {
        return toolError(
          'No existing brand guidelines found. Please fetch brand guidelines first.',
        );
      }

      // Convert textual image categories to numeric ones if provided
      let processedImageGuidelines: ImageGuidelines[] | null =
        currentBrandGuidelines.imageGuidelines;

      if (input.imageGuidelines) {
        if (input.imageGuidelines === null) {
          processedImageGuidelines = null;
        } else {
          // Start with existing guidelines or empty array
          const existingGuidelines = currentBrandGuidelines.imageGuidelines || [];

          // Convert input guidelines to proper enum format
          const inputGuidelines = input.imageGuidelines.map(
            (guideline: {imageCategory: AiBrandGuidelinesImageCategory; guidelines: string[]}) => ({
              imageCategory:
                getMatchingEnumValue(
                  AiBrandGuidelinesImageCategory,
                  BrandGuidelinesImageCategory,
                  guideline.imageCategory,
                ) ?? BrandGuidelinesImageCategory.ProductImage, // fallback to ProductImage if mapping fails
              guidelines: guideline.guidelines,
            }),
          );

          // Merge logic: update existing categories or add new ones
          const mergedGuidelines = [...existingGuidelines];

          inputGuidelines.forEach((inputGuideline) => {
            const existingIndex = mergedGuidelines.findIndex(
              (existing) => existing.imageCategory === inputGuideline.imageCategory,
            );

            if (existingIndex >= 0) {
              // Update existing category
              mergedGuidelines[existingIndex] = inputGuideline;
            } else {
              // Add new category
              mergedGuidelines.push(inputGuideline);
            }
          });

          processedImageGuidelines = mergedGuidelines;
        }
      }

      // Start with current brand guidelines and merge only what's provided
      const updatedBrandGuidelines: BrandGuidelines = {
        ...currentBrandGuidelines,
        brandIdentity:
          input.brandIdentity !== undefined
            ? ({
                ...currentBrandGuidelines.brandIdentity,
                ...(input.brandIdentity?.brandName !== undefined && {
                  brandName: input.brandIdentity.brandName,
                }),
                ...(input.brandIdentity?.brandDescription !== undefined && {
                  brandDescription: input.brandIdentity.brandDescription,
                }),
                ...(input.brandIdentity?.brandPersona !== undefined && {
                  brandPersona: input.brandIdentity.brandPersona,
                }),
                ...(input.brandIdentity?.brandValues !== undefined && {
                  brandValues: input.brandIdentity.brandValues,
                }),
                ...(input.brandIdentity?.targetIndustry !== undefined && {
                  targetIndustry: input.brandIdentity.targetIndustry,
                }),
                ...(input.brandIdentity?.usageContext !== undefined && {
                  usageContext: input.brandIdentity.usageContext,
                }),
              } as BrandIdentity)
            : currentBrandGuidelines.brandIdentity,
        brandVoiceGuidelines:
          input.brandVoiceGuidelines !== undefined
            ? ({
                ...currentBrandGuidelines.brandVoiceGuidelines,
                ...(input.brandVoiceGuidelines?.toneOfVoice !== undefined && {
                  toneOfVoice: input.brandVoiceGuidelines.toneOfVoice,
                }),
                ...(input.brandVoiceGuidelines?.editorialGuidelines !== undefined && {
                  editorialGuidelines: input.brandVoiceGuidelines.editorialGuidelines,
                }),
                ...(input.brandVoiceGuidelines?.editorialRestrictions !== undefined && {
                  editorialRestrictions: input.brandVoiceGuidelines.editorialRestrictions,
                }),
                ...(input.brandVoiceGuidelines?.brandPhrases !== undefined && {
                  brandPhrases: input.brandVoiceGuidelines.brandPhrases,
                }),
                ...(input.brandVoiceGuidelines?.grammarAndStyle !== undefined && {
                  grammarAndStyle: input.brandVoiceGuidelines.grammarAndStyle,
                }),
              } as BrandVoiceGuidelines)
            : currentBrandGuidelines.brandVoiceGuidelines,
        imageGuidelines: processedImageGuidelines,
        colors:
          input.colors !== undefined
            ? ({
                ...currentBrandGuidelines.colors,
                ...(input.colors?.colors !== undefined && {colors: input.colors.colors}),
                ...(input.colors?.colorUsageGuidelines !== undefined && {
                  colorUsageGuidelines: input.colors.colorUsageGuidelines,
                }),
              } as BrandColors)
            : currentBrandGuidelines.colors,
      };

      // Update local state instead of saving to DB
      store.dispatch(updateLocalBrandGuidelines(updatedBrandGuidelines));

      const updatedSections = [];

      if (input.brandIdentity !== undefined) updatedSections.push('brand identity');

      if (input.brandVoiceGuidelines !== undefined) updatedSections.push('brand voice guidelines');

      if (input.imageGuidelines !== undefined) updatedSections.push('image guidelines');

      if (input.colors !== undefined) updatedSections.push('colors');

      const sectionName = updatedSections.length > 0 ? updatedSections.join(', ') : 'no sections';

      return toolResult(
        `Successfully updated ${sectionName} of brand guidelines. Use the submit button to save your changes.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolError(`Failed to update brand guidelines: ${errorMessage}`);
    }
  }

  return tool({
    name: BrandGuidelinesAgentToolName.UpdateBrandGuidelines,
    displayName: 'Updating Brand Guidelines',
    description:
      'Updates local brand guidelines for the account. Changes will not be saved until the user clicks submit. Any optional section not provided will not be updated. If you want to clear a section, set it to null or empty array as applicable',
    inputSchema,
    execute,
  });
}

export default updateBrandGuidelinesTool;
